import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { useMutation } from '@apollo/client/react';
import { App } from 'antd';
import { useAuth } from 'react-oidc-context';
import { useSocketPrint } from '../ais/useAisCapabilities';
import { type AisPrintOutcome, watchAisPrintJob } from '../ais/watchAisPrintJob';
import { AisPrintKind, type PrintViaAisMutationVariables } from '../graphql/generated/graphql';
import { useSocket } from '../socket/useSocket';
import { fetchPrintable, NO_PRINT_PATH_HINT } from '../utils/print';
import { PRINT_VIA_AIS_MUTATION } from './usePrinter.gql';

export type PrintKind = 'label' | 'special-label' | 'a4';

export type PrintOptions = { title: string; copies?: number; requestId?: string; orderId?: string };

type PrintableLabel = {
  id: string;
  specimenId: string;
  testTubeId: string;
  location: string;
  specialPrinterRequired: boolean;
};

const AIS_KINDS: Record<PrintKind, { kind: AisPrintKind; contentType: string }> = {
  label: { kind: AisPrintKind.LABEL, contentType: 'application/zpl' },
  'special-label': { kind: AisPrintKind.SPECIAL_LABEL, contentType: 'application/zpl' },
  a4: { kind: AisPrintKind.A4, contentType: 'application/pdf' },
};

const NOT_HANDED_OVER = 'Der Druck konnte nicht an das AIS übergeben werden.';

// Apollo has no timeout of its own; without one a hung call would hold the watch and the unload guard forever
const HAND_OVER_TIMEOUT_MS = 60000;

// the print shows its own message, so the error link adds no second one
const OWN_ERROR_MESSAGE = { showsOwnError: true };

const WAITING_TEXT = 'Warte auf Druck über das AIS …';

const failureText = (outcome: Exclude<AisPrintOutcome, { state: 'printed' }>) => {
  if (outcome.state === 'noAnswer') {
    return 'Keine Rückmeldung vom AIS. Bitte am Drucker prüfen, bevor Sie erneut drucken.';
  }
  switch (outcome.code) {
    case 'printer-unavailable':
      return 'Das AIS hat keinen passenden Drucker für diesen Ausdruck.';
    case 'session-unknown':
      return 'Das AIS kennt diese Sitzung nicht mehr. Bitte labcomplete® erneut aus dem AIS öffnen.';
    case null:
      return 'Das AIS konnte nicht drucken.';
    default:
      return `Das AIS konnte nicht drucken (Code ${outcome.code}).`;
  }
};

/** A print that failed over the AIS; the printer has told the user already. */
class ShownPrintError extends Error {}

/** Whether the user was told of this print failure already, so the caller adds no message of its own. */
export const printErrorShown = (e: unknown) => e instanceof ShownPrintError;

const base64 = (buffer: ArrayBuffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  // in chunks, so a large PDF does not exceed the argument limit of fromCharCode
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
};

const byTube = (a: PrintableLabel, b: PrintableLabel) =>
  a.specimenId.localeCompare(b.specimenId) || a.testTubeId.localeCompare(b.testTubeId) || a.location.localeCompare(b.location);

const handOverError = (e: unknown) => new Error((CombinedGraphQLErrors.is(e) && e.errors[0]?.message) || NOT_HANDED_OVER);

// Uploads to labrador-api, which hands the job to the AIS session of this login; resolves once the AIS has printed it
const useAisPrint = () => {
  const { socket } = useSocket();
  const { message } = App.useApp();
  const [printViaAis] = useMutation(PRINT_VIA_AIS_MUTATION);

  const handOver = async (variables: PrintViaAisMutationVariables) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), HAND_OVER_TIMEOUT_MS);
    const timedOut = new Promise<never>((_, reject) => {
      controller.signal.addEventListener('abort', () => reject(new Error(NOT_HANDED_OVER)));
    });
    try {
      const call = printViaAis({
        variables,
        context: { ...OWN_ERROR_MESSAGE, fetchOptions: { signal: controller.signal } },
      });
      return (await Promise.race([call, timedOut])).data?.printViaAis;
    } finally {
      clearTimeout(timer);
    }
  };

  return async (buffer: ArrayBuffer, kind: PrintKind, options: PrintOptions) => {
    const { kind: aisKind, contentType } = AIS_KINDS[kind];
    const variables = {
      kind: aisKind,
      title: options.title,
      content: base64(buffer),
      contentType,
      copies: options.copies ?? 1,
      requestId: options.requestId,
      orderId: options.orderId,
    };
    // listening before the call, so a result that overtakes its answer is not missed
    const watch = watchAisPrintJob(socket);
    const job = await handOver(variables).catch(e => {
      watch.cancel();
      throw handOverError(e);
    });
    if (!job) {
      watch.cancel();
      throw new Error(NOT_HANDED_OVER);
    }
    const key = `ais-print-${job.jobId}`;
    message.loading({ content: WAITING_TEXT, key, duration: 0 });
    const outcome = await watch.wait(job);
    message.destroy(key);
    if (outcome.state !== 'printed') {
      throw new Error(failureText(outcome));
    }
  };
};

/** Whether this tab has a print path: the desktop client, or an AIS session that prints over its socket. */
export const useCanPrint = (): boolean => {
  const socketPrint = useSocketPrint();
  return !!window.nativeApi || socketPrint;
};

/**
 * The one way to print: the desktop client where it runs, else the AIS of a session that prints over its socket.
 * `print` resolves once the AIS has printed the job, so the caller marks it printed afterwards, as with the desktop client.
 * An AIS failure is shown here, once; the caller checks `printErrorShown` before adding a message of its own.
 */
export const usePrinter = () => {
  const auth = useAuth();
  const { message } = App.useApp();
  const canPrint = useCanPrint();
  const printViaAis = useAisPrint();

  const print = async (buffer: ArrayBuffer, kind: PrintKind, options: PrintOptions) => {
    if (window.nativeApi) {
      for (let copy = 0; copy < (options.copies ?? 1); copy++) {
        await window.nativeApi.print(buffer, kind);
      }
      return;
    }
    if (!canPrint) {
      throw new Error(NO_PRINT_PATH_HINT);
    }
    try {
      await printViaAis(buffer, kind, options);
    } catch (e) {
      const text = (e as Error).message;
      // jobs that run side by side and fail alike share one message
      message.error({ content: text, key: text });
      throw new ShownPrintError(text);
    }
  };

  /** Fetches the labels, merged into one request per printer, which speeds up printing; false when a download failed. */
  const printLabels = async (labels: PrintableLabel[], options: Omit<PrintOptions, 'title'>) => {
    const batches = { label: [] as ArrayBuffer[], 'special-label': [] as ArrayBuffer[] };
    let complete = true;
    try {
      for (const label of [...labels].sort(byTube)) {
        const buffer = await fetchPrintable(`/rest/order-label/${label.id}/download`, auth.user?.access_token);
        batches[label.specialPrinterRequired ? 'special-label' : 'label'].push(buffer);
      }
    } catch {
      complete = false;
    }
    for (const [kind, title] of [
      ['label', 'Etiketten'],
      ['special-label', 'Spezialetiketten'],
    ] as const) {
      if (batches[kind].length > 0) {
        await print(await new Blob(batches[kind]).arrayBuffer(), kind, { ...options, title });
      }
    }
    return complete;
  };

  return { canPrint, print, printLabels };
};
